import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/dendros/Navbar";
import SiteFooter from "@/components/dendros/SiteFooter";
import {
    ArrowRight,
    Activity,
    Network,
    Zap,
    Sprout,
    ShieldCheck,
    ScrollText,
    Cpu,
    Chrome,
    KeyRound,
} from "lucide-react";

const STATES = [
    { id: "00", title: "Observer", icon: Activity, desc: "Detects Salesforce Account pages. Scans contacts in real time. Zero footprint elsewhere." },
    { id: "01", title: "Audit", icon: Network, desc: "Cross-checks every contact against verified employment signals. RED for ghosts, AMBER for stale titles, EMERALD for verified." },
    { id: "02", title: "Remove", icon: Zap, desc: "Tier 1 manual removal, Tier 2 auto-remove. Writes Contact.Status = 'Inactive' through the rep's active session." },
    { id: "03", title: "Heal", icon: Sprout, desc: "Surfaces ICP-fit replacements with confidence scores. One-click inject creates the new Contact directly in Salesforce." },
];

const ARCH = [
    { title: "Frontend", sub: "Chrome Extension", body: "Injected sidebar and toolbar popup. Shared component tree. Activates only on Salesforce Account pages." },
    { title: "Backend", sub: "Dendros Orchestration", body: "Proprietary signal enrichment and identity resolution. Every action is immutably logged for SOC-2 compliance." },
    { title: "Bridge", sub: "Session-Native Write-back", body: "Reads the active Salesforce session to write changes back — no token storage, no external credentials." },
];

const COMPLIANCE = [
    { icon: ScrollText, title: "SOC-2 Audit Trail", body: "Every action — sync, delete, inject — emits an immutable log entry to your Salesforce org for full compliance traceability." },
    { icon: ShieldCheck, title: "ISO 27001 Data Handling", body: "Zero credentials stored in the browser. All sensitive keys are server-side vaulted under strict access controls." },
    { icon: Zap, title: "Zero-Footprint Activation", body: "Extension only activates on Salesforce domains. Other tabs see nothing — performance untouched." },
];

const STATS = [
    { v: "62%", l: "Avg pipeline that's ghost contacts" },
    { v: "11min", l: "From sync to first remove" },
    { v: "100%", l: "Salesforce-native write-back" },
    { v: "0", l: "Credentials stored in browser" },
];

export default function Pulse() {
    return (
        <div className="dendros-radial min-h-screen text-white relative" data-testid="pulse-page">
            <div className="dendros-grain" />
            <Navbar />

            {/* Hero */}
            <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-28 lg:pt-32 pb-16 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-7">
                    <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#10b981] border border-[#10b981]/40 px-3 py-1.5 rounded-full mb-7">
                        <Chrome size={11} /> Dendros
                    </div>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[0.98]">
                        <span className="text-[#10b981]">Dendros.</span>
                        <br />
                        The Dendros engine, embedded in Salesforce.
                    </h1>
                    <p className="font-body text-lg text-zinc-400 mt-7 max-w-xl leading-relaxed">
                        Dendros sits on top of the Salesforce Lightning UI — no LWC, no deployment cycles. Reps run an audit, remove ghosts, and heal ICP gaps without ever leaving the record.
                    </p>
                    <div className="flex items-center gap-3 mt-9 flex-wrap">
                        <Link to="/salesforce" className="btn-emerald" data-testid="pulse-launch-demo">
                            Launch Salesforce Demo <ArrowRight size={14} />
                        </Link>
                        <Link to="/popup" className="btn-outline-d" data-testid="pulse-view-popup">
                            View Toolbar Popup
                        </Link>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14">
                        {STATS.map((s) => (
                            <div
                                key={s.l}
                                className="border-l border-[#10b981]/30 pl-3"
                                data-testid={`pulse-stat-${s.l.replace(/\s/g, "-").slice(0, 20)}`}
                            >
                                <div className="font-display text-2xl font-semibold text-[#10b981]">{s.v}</div>
                                <div className="font-body text-[11px] text-zinc-500 mt-1 leading-relaxed">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Terminal trace */}
                <div className="lg:col-span-5">
                    <div className="dendros-card p-0 overflow-hidden">
                        <div className="px-4 py-2.5 border-b border-white/10 flex items-center gap-2 bg-white/[0.02]">
                            <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                            <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                            <span className="w-2.5 h-2.5 rounded-full bg-[#10b981]" />
                            <span className="font-mono text-[10px] text-zinc-500 ml-3 uppercase tracking-widest">dendros · audit-trace</span>
                        </div>
                        <pre className="font-mono text-[12px] leading-7 text-zinc-300 p-5 whitespace-pre-wrap" data-testid="pulse-trace">
{`> SYNC_INITIATED  account=Quantum Logistics
✓ Scanned 5 contacts
> AUDIT.IDENTITY  engine=dendros
`}<span className="text-[#fca5a5]">{`✗ GHOST  Brandon Wells → Flexport
✗ GHOST  Yuki Tanaka  → Convoy
`}</span>{`> ICP_GAP  missing=[VP_SALES, CMO, REVOPS]
> HEAL.PIPELINE  engine=dendros
`}<span className="text-[#34d399]">{`✓ FOUND  Jonas Lindqvist · 89% match
✓ FOUND  Priya Raghavan  · 87% match
`}</span>{`> AWAITING  rep.confirmation`}
                        </pre>
                    </div>
                    <div className="font-mono text-[10px] text-zinc-500 mt-3 uppercase tracking-widest">Live audit trace · streamed from backend</div>
                </div>
            </section>

            {/* Protocol */}
            <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-20 section-divider">
                <div className="max-w-3xl">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#10b981] mb-3">Master Execution Protocol</div>
                    <h2 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight">Four states. One on-demand loop.</h2>
                    <p className="font-body text-zinc-400 mt-5 max-w-2xl text-lg leading-relaxed">
                        Dendros doesn't ingest your CRM. It activates only when the rep is on a Salesforce Account page — runs the audit, takes one action, and disappears.
                    </p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
                    {STATES.map((s) => {
                        const Icon = s.icon;
                        return (
                            <div key={s.id} className="dendros-card p-6" data-testid={`state-card-${s.id}`}>
                                <div className="flex items-center justify-between mb-5">
                                    <div className="w-10 h-10 rounded-md bg-[#10b981]/10 border border-[#10b981]/30 flex items-center justify-center">
                                        <Icon size={18} className="text-[#10b981]" />
                                    </div>
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">State {s.id}</span>
                                </div>
                                <h3 className="font-display text-xl font-semibold tracking-tight">{s.title}</h3>
                                <p className="font-body text-sm text-zinc-400 mt-2 leading-relaxed">{s.desc}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Architecture */}
            <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-20 section-divider">
                <div className="grid lg:grid-cols-2 gap-12">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#10b981] mb-3">Deployment Architecture</div>
                        <h2 className="font-display text-4xl font-semibold tracking-tight">Sit on top. Don't sit inside.</h2>
                        <p className="font-body text-zinc-400 mt-5 leading-relaxed">
                            No Lightning Web Component. No release cycles. The Dendros extension injects a sidebar only on Salesforce Account pages and writes back through the rep's active session — nothing installed on your org.
                        </p>
                    </div>
                    <div className="space-y-3">
                        {ARCH.map((r) => (
                            <div key={r.title} className="dendros-card p-5 flex items-start gap-4">
                                <div className="w-9 h-9 rounded-md bg-zinc-900 border border-white/10 flex items-center justify-center">
                                    <Cpu size={16} className="text-[#10b981]" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-baseline justify-between">
                                        <div className="font-display text-lg font-semibold">{r.title}</div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{r.sub}</div>
                                    </div>
                                    <p className="font-body text-sm text-zinc-400 mt-1.5 leading-relaxed">{r.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Compliance */}
            <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-20 section-divider">
                <div className="max-w-3xl">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#10b981] mb-3">Compliance &amp; Security</div>
                    <h2 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight">Built for the audit, not the demo.</h2>
                </div>
                <div className="grid md:grid-cols-3 gap-4 mt-10">
                    {COMPLIANCE.map((c) => {
                        const Icon = c.icon;
                        return (
                            <div key={c.title} className="dendros-card p-6">
                                <div className="w-10 h-10 rounded-md bg-[#10b981]/10 border border-[#10b981]/30 flex items-center justify-center">
                                    <Icon size={20} className="text-[#10b981]" />
                                </div>
                                <h3 className="font-display text-xl font-semibold tracking-tight mt-5">{c.title}</h3>
                                <p className="font-body text-sm text-zinc-400 mt-2 leading-relaxed">{c.body}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* CTA */}
            <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-20 section-divider">
                <div className="dendros-card p-10 lg:p-14 text-center max-w-3xl mx-auto relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#10b981]/5 via-transparent to-transparent pointer-events-none" />
                    <KeyRound size={28} className="text-[#10b981] mx-auto relative" />
                    <h2 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight mt-5 relative">Stop selling to ghosts.</h2>
                    <p className="font-body text-zinc-400 mt-5 max-w-xl mx-auto leading-relaxed relative">
                        Run a live audit against four pre-loaded accounts. Watch Dendros find ghosts, remove them, and heal the pipeline — in under a minute.
                    </p>
                    <div className="flex items-center justify-center gap-3 mt-8 flex-wrap relative">
                        <Link to="/salesforce" className="btn-emerald" data-testid="pulse-cta-bottom">
                            Launch Demo <ArrowRight size={14} />
                        </Link>
                        <Link to="/popup" className="btn-outline-d">View Popup</Link>
                        <Link to="/" className="btn-outline-d">Back to Dendros.ai</Link>
                    </div>
                </div>
            </section>

            <SiteFooter />
        </div>
    );
}
